import React, { useState } from 'react';
import {
  AutoComplete,
  Checkbox,
  Input,
  notification,
  Select,
  Switch,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  AnyAssetSubType,
  AssetConfig,
  BaseInputConfig,
  Brand,
  CustomField,
  FormGroup,
  Grid,
  Series,
  createUrl,
  defaultResponseHandler,
  openNotificationWithIcon,
  useAssetConfig,
  useCustodianQuery,
  useLocationQuery,
  useRolesQuery,
} from '@mss/mss-component-library';
import { useQuery } from '@tanstack/react-query';
import { CustomFieldModels, useCustomFieldsQuery } from '../../services/queries';
import Fetch from '../../../common/util/_fetch';
import Button from '../../../common/Button';
import BulkCustomFields from './BulkCustomFields';
import { apiSubtypeToConfigKey, subtypeToCoreAssetName } from './assetTypeMapping';
import { getBulkNonEditableFields } from './bulkEditFieldRules';
import type { ExportAssetType } from '../../util/useAddPermissions';

type Props = {
  selectedAssets: any[];
  onPrevious: () => void;
  onComplete: () => void;
  type: ExportAssetType;
  subtypeKey: string;
  /** When asset type has only one subtype, use this for config lookup when API omits the field. */
  singleSubtypeFallback?: string;
  customFieldModel: CustomFieldModels;
};

type FieldValues = Record<string, any>;

// Fields whose options are subtype-specific (e.g. brand options differ per network subtype).
// Greyed out when multiple subtypes are selected, even if all subtypes share the field.
const SUBTYPE_OPTION_FIELDS = new Set(['brand', 'series']);

const BulkEditTab: React.FC<Props> = ({
  selectedAssets,
  onPrevious,
  onComplete,
  type,
  subtypeKey,
  singleSubtypeFallback,
  customFieldModel,
}) => {
  const { t } = useTranslation(['asset', 'common']);
  const queryClient = useQueryClient();
  const { config } = useAssetConfig();
  const [enabledFields, setEnabledFields] = useState<Set<string>>(new Set());
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [enabledCustomFields, setEnabledCustomFields] = useState<Set<string>>(
    new Set()
  );
  const [customFieldValues, setCustomFieldValues] = useState<FieldValues>({});
  const [submitting, setSubmitting] = useState(false);

  const { data: roles } = useRolesQuery();
  const { data: locations } = useLocationQuery();
  const { data: custodians } = useCustodianQuery();
  const { data: customFields } = useCustomFieldsQuery(customFieldModel);

  // Determine which subtypes are represented in the selection.
  const uniqueSubtypes = [
    ...new Set(selectedAssets.map(a => a[subtypeKey] as AnyAssetSubType)),
  ];
  const isSingleSubtype = uniqueSubtypes.length === 1;
  const singleSubtype = isSingleSubtype ? uniqueSubtypes[0] : null;
  const effectiveSingleSubtype = singleSubtype ?? singleSubtypeFallback;
  const configSubtype = effectiveSingleSubtype
    ? apiSubtypeToConfigKey(type, effectiveSingleSubtype)
    : null;

  const nonEditableFields = getBulkNonEditableFields(type, effectiveSingleSubtype ?? null);

  // Derive configure inputs:
  // - Single subtype: full inputs for that subtype.
  // - Mixed subtypes: intersection of inputs present in ALL selected subtypes.
  const configureInputs: BaseInputConfig[] = (() => {
    if ((isSingleSubtype || singleSubtypeFallback) && configSubtype) {
      return (config.assets[configSubtype as AnyAssetSubType] as AssetConfig<any>)?.form.configure.inputs ?? [];
    }
    const selectedSubtypeInputs = uniqueSubtypes.map(st =>
      (config.assets[apiSubtypeToConfigKey(type, st) as AnyAssetSubType] as AssetConfig<any>)
        ?.form.configure.inputs ?? []
    );
    if (selectedSubtypeInputs.length === 0) return [];
    return selectedSubtypeInputs.reduce(
      (common: BaseInputConfig[], inputs: BaseInputConfig[]) =>
        common.filter(f => inputs.find(i => i.fieldName === f.fieldName))
    );
  })();

  const bulkEditableCustomFields = (customFields ?? []).filter(
    (cf: CustomField) => cf.bulk_editable !== false
  );

  const toggleField = (fieldName: string) => {
    const next = new Set(enabledFields);
    const nextValues = { ...fieldValues };
    if (next.has(fieldName)) {
      next.delete(fieldName);
      delete nextValues[fieldName];
      // Unchecking brand also removes series (they're linked)
      if (fieldName === 'brand') {
        next.delete('series');
        delete nextValues['series'];
      }
    } else {
      next.add(fieldName);
      // Checking brand also enables series
      if (fieldName === 'brand') {
        next.add('series');
      }
    }
    setEnabledFields(next);
    setFieldValues(nextValues);
  };

  const setValue = (fieldName: string, value: any) => {
    setFieldValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const toggleCustomField = (slug: string) => {
    const next = new Set(enabledCustomFields);
    if (next.has(slug)) {
      next.delete(slug);
      const nextValues = { ...customFieldValues };
      delete nextValues[slug];
      setCustomFieldValues(nextValues);
    } else {
      next.add(slug);
    }
    setEnabledCustomFields(next);
  };

  const setCustomFieldValue = (slug: string, value: any) => {
    setCustomFieldValues(prev => ({ ...prev, [slug]: value }));
  };

  const buildFields = () => {
    const fields: Record<string, any> = {};

    for (const fieldName of enabledFields) {
      if (fieldName === 'brand') {
        // brand is UI-only (filters series options); never sent to backend
        continue;
      } else if (fieldName === 'series') {
        // series FK is stored as series_id on the model
        fields['series_id'] = fieldValues[fieldName] ?? null;
      } else if (fieldName === 'custodian') {
        const val = fieldValues[fieldName];
        if (val === null || val === undefined || val === '') {
          fields.custodian = null;
        } else {
          const allCustodians = custodians?.flatMap(
            (g: any) => g.options ?? []
          );
          const match = allCustodians?.find((c: any) => c.value === val);
          if (match) {
            fields.custodian = { type: match.type ?? 'user', id: val };
          }
        }
      } else {
        fields[fieldName] = fieldValues[fieldName] ?? null;
      }
    }

    if (enabledCustomFields.size > 0) {
      const cfv: Record<string, any> = {};
      for (const slug of enabledCustomFields) {
        cfv[slug] = customFieldValues[slug] ?? null;
      }
      fields.custom_field_values = cfv;
    }

    return fields;
  };

  const groupAssetsByType = () => {
    const groups: Record<string, string[]> = {};
    for (const asset of selectedAssets) {
      const subtype = asset[subtypeKey] ?? 'unknown';
      const configKey = apiSubtypeToConfigKey(type, subtype);
      const coreAssetName = subtypeToCoreAssetName(configKey);
      if (!groups[coreAssetName]) groups[coreAssetName] = [];
      groups[coreAssetName].push(asset.id);
    }
    return groups;
  };

  const handleSubmit = async () => {
    if (enabledFields.size === 0 && enabledCustomFields.size === 0) return;

    setSubmitting(true);
    try {
      const fields = buildFields();
      const groups = groupAssetsByType();
      let totalUpdated = 0;

      for (const [coreAssetName, assetIds] of Object.entries(groups)) {
        const response = await Fetch.post(
          `/api/core/asset-type/${coreAssetName}/bulk/edit/`,
          { body: JSON.stringify({ asset_ids: assetIds, fields }) }
        );

        if (response.ok) {
          const data = await response.json();
          totalUpdated += data.updated;
        } else {
          const errorData = await response.json().catch(() => null);
          const message =
            errorData?.detail ||
            (Array.isArray(errorData)
              ? errorData.join(', ')
              : JSON.stringify(errorData)) ||
            t('An error occurred');
          openNotificationWithIcon(
            notification.error,
            t('Bulk edit failed'),
            message
          );
          return;
        }
      }

      openNotificationWithIcon(
        notification.success,
        t('Bulk edit completed'),
        t('{{count}} asset(s) updated successfully.', {
          count: totalUpdated,
        })
      );
      queryClient.invalidateQueries(['fieldAssets']);
      queryClient.invalidateQueries(['bulkAssets']);
      onComplete();
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Bulk edit failed'),
        t('An unexpected error occurred. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const brandEnabled = enabledFields.has('brand');
  const seriesMissingWhenBrandEnabled = brandEnabled && !fieldValues['series'];

  const hasSelectedFields =
    enabledFields.size > 0 || enabledCustomFields.size > 0;

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-medium mb-1">{t('Edit assets')}</h3>
        <p className="text-gray-500 text-sm">
          {t(
            'Adjust the fields that needs to be added for all selected assets. If a field is not adjusted in this step, the value will remain the same.'
          )}
        </p>
        <p className="text-gray-400 text-sm">
          {t('Greyed out fields cannot be edited in bulk.')}
        </p>
      </div>

      <Grid.Row>
        {configureInputs.filter(f => f.fieldName !== 'description').map(field => {
          const isNonEditable =
            nonEditableFields.has(field.fieldName) ||
            (SUBTYPE_OPTION_FIELDS.has(field.fieldName) && !isSingleSubtype);
          const isEnabled = enabledFields.has(field.fieldName);
          const width = field.width ?? 4;

          // Series checkbox is hidden -- it's controlled by the brand checkbox.
          const hideCheckbox = isNonEditable || field.fieldName === 'series';

          return (
            <Grid.Col width={width} key={field.fieldName}>
              <FormGroup
                label={t(field.label)}
                input={
                  <div className="flex items-start gap-2">
                    {!hideCheckbox && (
                      <Checkbox
                        checked={isEnabled}
                        onChange={() => toggleField(field.fieldName)}
                        className="mt-1"
                      />
                    )}
                    <div className="flex-1">
                      <FieldInput
                        field={field}
                        disabled={isNonEditable || !isEnabled}
                        value={fieldValues[field.fieldName]}
                        onChange={val => {
                          setValue(field.fieldName, val);
                          // Changing brand clears the linked series selection
                          if (field.fieldName === 'brand') {
                            setValue('series', undefined);
                          }
                        }}
                        roles={roles}
                        locations={locations}
                        custodians={custodians}
                        subtype={(effectiveSingleSubtype as AnyAssetSubType) ?? null}
                        selectedBrand={fieldValues['brand']}
                        t={t}
                      />
                    </div>
                  </div>
                }
              />
            </Grid.Col>
          );
        })}

        <BulkCustomFields
          customFields={bulkEditableCustomFields}
          enabledFields={enabledCustomFields}
          values={customFieldValues}
          onToggle={toggleCustomField}
          onChange={setCustomFieldValue}
          custodians={custodians}
        />

        {configureInputs.filter(f => f.fieldName === 'description').map(field => {
          const isEnabled = enabledFields.has(field.fieldName);
          return (
            <Grid.Col width={12} key={field.fieldName}>
              <FormGroup
                label={t(field.label)}
                input={
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={isEnabled}
                      onChange={() => toggleField(field.fieldName)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Input.TextArea
                        rows={4}
                        disabled={!isEnabled}
                        value={fieldValues[field.fieldName] ?? ''}
                        onChange={e => setValue(field.fieldName, e.target.value)}
                        placeholder={t(
                          'If required, you can enter a description about the asset application here...'
                        )}
                      />
                    </div>
                  </div>
                }
              />
            </Grid.Col>
          );
        })}
      </Grid.Row>

      <div className="flex justify-between mt-6">
        <Button onClick={onPrevious}>{t('Previous')}</Button>
        <Button
          variant="primary"
          disabled={!hasSelectedFields || seriesMissingWhenBrandEnabled || submitting}
          loading={submitting}
          onClick={handleSubmit}
        >
          {t('Submit')}
        </Button>
      </div>
    </div>
  );
};

type FieldInputProps = {
  field: BaseInputConfig;
  disabled: boolean;
  value: any;
  onChange: (value: any) => void;
  roles: any;
  locations: any;
  custodians: any;
  subtype: AnyAssetSubType | null;
  selectedBrand?: number;
  t: (key: string, options?: any) => string;
};

const FieldInput: React.FC<FieldInputProps> = ({
  field,
  disabled,
  value,
  onChange,
  roles,
  locations,
  custodians,
  subtype,
  selectedBrand,
  t,
}) => {
  switch (field.type) {
    case 'role':
      return (
        <AutoComplete
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ?? ''}
          onChange={onChange}
          placeholder={t('Enter or select a role')}
          options={roles}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      );

    case 'custodian': {
      const flatOptions =
        custodians?.flatMap((group: any) =>
          (group.options ?? []).map((opt: any) => ({
            label: `${opt.label}`,
            value: opt.value,
            type: opt.type,
          }))
        ) ?? [];
      return (
        <Select
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          placeholder={t('Please select one...')}
          showSearch
          allowClear
          optionFilterProp="label"
          options={flatOptions}
        />
      );
    }

    case 'criticality':
      return (
        <Select
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          placeholder={t('Please select one...')}
          allowClear
          options={[
            { label: t('Low'), value: 'low' },
            { label: t('Medium'), value: 'medium' },
            { label: t('High'), value: 'high' },
          ]}
        />
      );

    case 'location':
      return (
        <AutoComplete
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ?? ''}
          onChange={onChange}
          placeholder={t('Location')}
          options={locations}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      );

    case 'switch':
      return (
        <Switch
          disabled={disabled}
          checked={!!value}
          onChange={onChange}
        />
      );

    case 'brand':
      return (
        <BrandSeriesSelect
          fieldName="brand"
          subtype={subtype}
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
        />
      );

    case 'series':
      return (
        <BrandSeriesSelect
          fieldName="series"
          subtype={subtype}
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          selectedBrand={selectedBrand}
        />
      );

    case 'timezone':
      return (
        <TimezoneBulkSelect
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          t={t}
        />
      );

    case 'opc_server_group':
      return (
        <OPCServerGroupBulkSelect
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          t={t}
        />
      );

    case 'string':
    default:
      return (
        <Input
          disabled={disabled}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={t(field.label)}
        />
      );
  }
};

type TimezoneBulkSelectProps = {
  disabled: boolean;
  value: any;
  onChange: (value: any) => void;
  t: (key: string, options?: any) => string;
};

const TimezoneBulkSelect: React.FC<TimezoneBulkSelectProps> = ({
  disabled,
  value,
  onChange,
  t,
}) => {
  const { data: options = [] } = useQuery<{ label: string; value: string }[]>({
    queryKey: ['timezones'],
    queryFn: async () => {
      const { data } = await defaultResponseHandler<string[]>({
        t: t as any,
        request: Fetch.get('/api/timezones/'),
      });
      return (data ?? []).map(tz => ({
        label: tz.replace(/_/g, ' '),
        value: tz,
      }));
    },
  });
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      value={value ?? undefined}
      onChange={onChange}
      placeholder={t('Select a timezone')}
      showSearch
      allowClear
      optionFilterProp="label"
      options={options}
    />
  );
};

type OPCServerGroupBulkSelectProps = {
  disabled: boolean;
  value: any;
  onChange: (value: any) => void;
  t: (key: string, options?: any) => string;
};

type OPCServerOption = { id: string; name: string };

const OPCServerGroupBulkSelect: React.FC<OPCServerGroupBulkSelectProps> = ({
  disabled,
  value,
  onChange,
  t,
}) => {
  const { data: options = [] } = useQuery<{ label: string; value: string }[]>({
    queryKey: ['opc-config-server-group'],
    queryFn: async () => {
      const res = await defaultResponseHandler<OPCServerOption[]>({
        t: t as any,
        request: Fetch.get('/api/opc-config-server-group/'),
      });
      const list = Array.isArray(res?.data) ? res.data : (res as any)?.results ?? [];
      return list.map((item: OPCServerOption) => ({
        label: item.name || item.id,
        value: item.id,
      }));
    },
  });
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      value={value ?? undefined}
      onChange={onChange}
      placeholder={t('Select OPC Server Group')}
      showSearch
      allowClear
      optionFilterProp="label"
      options={options}
    />
  );
};

type BrandSeriesSelectProps = {
  fieldName: 'brand' | 'series';
  subtype: AnyAssetSubType | null;
  disabled: boolean;
  value: any;
  onChange: (value: any) => void;
  selectedBrand?: number;
};

const BrandSeriesSelect: React.FC<BrandSeriesSelectProps> = ({
  fieldName,
  subtype,
  disabled,
  value,
  onChange,
  selectedBrand,
}) => {
  const { t } = useTranslation(['asset', 'common']);
  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ['network', 'brands', subtype],
    enabled: !!subtype,
    queryFn: async () =>
      (
        await defaultResponseHandler<Brand[]>({
          t,
          request: Fetch.get(
            createUrl({ assetType: 'network' }, `brands/?series__type=${subtype}`, true)
          ),
        })
      ).data ?? [],
  });

  if (fieldName === 'brand') {
    const options = brands.map(b => ({ value: b.id, label: b.name }));
    return (
      <Select
        style={{ width: '100%' }}
        disabled={disabled}
        value={value}
        onChange={onChange}
        placeholder={`${t('Please select one')}...`}
        showSearch
        allowClear
        optionFilterProp="label"
        options={options}
      />
    );
  }

  // Series: filter by selected brand if one is chosen, otherwise show all
  const matchingBrand = brands.find(b => b.id === selectedBrand);
  const seriesOptions = (matchingBrand ? [matchingBrand] : brands).flatMap(b =>
    b.series_set.map((s: Series) => ({ value: s.id, label: s.name }))
  );
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      value={value}
      onChange={onChange}
      placeholder={
        !selectedBrand
          ? `${t('Select a brand first')}...`
          : `${t('Please select one')}...`
      }
      showSearch
      allowClear
      optionFilterProp="label"
      options={seriesOptions}
    />
  );
};

export default BulkEditTab;
